import type { Rect } from '../core/math';
import type { World } from '../game/world';
import type { Renderer } from '../gfx/renderer';
import type { Body } from '../world/physics';

export type EntityKind = 'enemy' | 'item' | 'coin' | 'orb' | 'fx' | 'shell' | 'goal';

/** Base class for everything that lives in the world and is not a tile. */
export abstract class Entity implements Body {
  x = 0;
  y = 0;
  w = 16;
  h = 16;
  vx = 0;
  vy = 0;
  onGround = false;
  hitCeiling = false;
  hitLeft = false;
  hitRight = false;
  passOneWay = false;

  alive = true;
  /** Entities start asleep and wake when the camera gets close. */
  awake = false;
  /** Never sleep again once woken (items, projectiles). */
  alwaysAwake = false;
  /** Facing: -1 left, 1 right. */
  facing = -1;
  abstract readonly kind: EntityKind;
  /** Set for enemies that can be defeated. */
  dying = false;
  /** Draw order: lower first. */
  z = 0;
  /** Original spawn position, used when a level is restarted. */
  homeX = 0;
  homeY = 0;

  constructor(x: number, y: number, w: number, h: number) {
    this.x = x;
    this.y = y;
    this.w = w;
    this.h = h;
    this.homeX = x;
    this.homeY = y;
  }

  get rect(): Rect {
    return { x: this.x, y: this.y, w: this.w, h: this.h };
  }

  get centerX(): number {
    return this.x + this.w / 2;
  }

  get centerY(): number {
    return this.y + this.h / 2;
  }

  get bottom(): number {
    return this.y + this.h;
  }

  abstract update(dt: number, world: World): void;
  abstract draw(r: Renderer, world: World): void;

  /** Player landed on top. Return true if the stomp was consumed (player bounces). */
  onStomp?(world: World): boolean;
  /** Player touched from the side / below. */
  onTouch?(world: World): void;
  /** Hit by an energy orb. Return true if the orb should be destroyed. */
  onOrb?(world: World): boolean;
  /** The tile underneath was bumped by the player. */
  onBlockBump?(world: World): void;
  /** Hit by a sliding shell. */
  onShellHit?(world: World, dir: number): void;
  /** Killed by a star-powered player touching it. */
  onPrism?(world: World): void;
}
